import react from "react";
import { createComponent } from "@lit/react";
import { MdOutlinedButton } from "@material/web/button/outlined-button";
import { MdFilledButton } from "@material/web/button/filled-button";
import { MdElevatedButton } from "@material/web/button/elevated-button";
import { MdFilledTonalButton } from "@material/web/button/filled-tonal-button";
import { MdTextButton } from "@material/web/button/text-button";

export const LitOutlinedButton = createComponent({
  tagName: "md-outlined-button",
  elementClass: MdOutlinedButton,
  react
});

export const LitFilledButton = createComponent({
  tagName: "md-filled-button",
  elementClass: MdFilledButton,
  react
});

export const LitElevatedButton = createComponent({
  tagName: "md-elevated-button",
  elementClass: MdElevatedButton,
  react
});

export const LitFilledTonalButton = createComponent({
  tagName: "md-filled-tonal-button",
  elementClass: MdFilledTonalButton,
  react
});

export const LitTextButton = createComponent({
  tagName: "md-text-button",
  elementClass: MdTextButton,
  react
});

interface SquaredButtonProps {
  disabled?: boolean;
  onClick?: () => void;
  trailingIcon?: boolean;
  children?: react.ReactNode;
  style?: react.CSSProperties;
  "data-testid"?: string;
}

const squaredButtonClassName =
  "cursor-pointer relative rounded-lg bg-transparent border border-solid border-sys-brand-outline-variant hover:bg-state-layers-brand-on-surface-opacity-0-08 focus:bg-state-layers-brand-on-surface-opacity-0-08 focus:outline-2 focus:outline-sys-brand-primary focus:outline-offset-2 active:bg-state-layers-brand-on-surface-opacity-0-12 disabled:opacity-50 disabled:cursor-not-allowed disabled:[&_md-icon]:cursor-not-allowed [&_md-icon]:cursor-pointer transition-all duration-200 flex-shrink-0";

function hasSquaredTextLabel(children: react.ReactNode): boolean {
  return react.Children.toArray(children).some((child) => {
    if (typeof child === "number") return true;
    if (typeof child === "string") return child.trim().length > 0;
    return false;
  });
}

export const LitSquaredButton = react.forwardRef<HTMLButtonElement, SquaredButtonProps>(
  ({ disabled, onClick, trailingIcon, children, style, "data-testid": testId, ...props }, ref) => {
    const innerPadding = hasSquaredTextLabel(children) ? "pl-2 pr-4 py-2.5" : "p-2";

    return (
      <button
        ref={ref}
        disabled={disabled}
        onClick={onClick}
        data-testid={testId}
        style={style}
        className={squaredButtonClassName}
        {...props}
      >
        <div className="box-border flex flex-row items-center justify-center overflow-hidden p-0 size-full">
          <div
            className={`box-border flex flex-row gap-1 items-center justify-center relative shrink-0 text-title-small ${innerPadding}`}
          >
            {children}
          </div>
        </div>
      </button>
    );
  }
);

export const LitSquaredSmallButton = react.forwardRef<HTMLButtonElement, SquaredButtonProps>(
  ({ disabled, onClick, trailingIcon, children, style, "data-testid": testId, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled}
        onClick={onClick}
        data-testid={testId}
        style={style}
        className={squaredButtonClassName}
        {...props}
      >
        <div className="box-border flex flex-row items-center justify-center overflow-hidden p-0 size-full">
          <div className="box-border flex flex-row gap-1 items-center justify-center px-2 py-1.5 relative shrink-0 text-title-small">
            {children}
          </div>
        </div>
      </button>
    );
  }
);
